import { address, createSolanaRpc, type Rpc, type SolanaRpcApi } from "@solana/kit";
import { fetchMaybeToken } from "@solana-program/token";
import { fetchMaybeToken as fetchMaybeToken2022 } from "@solana-program/token-2022";
import {
  addressSlicer,
  type BaseTokenInfo,
  CHAIN_NAMESPACES,
  significantDigits,
  TRANSACTION_TYPES,
  type TransactionStatus,
} from "@toruslabs/base-controllers";
import { CONTRACT_TYPE_ERC721, CONTRACT_TYPE_ERC1155, type EthereumTransactionMeta } from "@toruslabs/ethereum-controllers";
import { type SolanaTransactionMeta } from "@toruslabs/solana-controllers";
import BigNumber from "bignumber.js";
import { formatEther, getAddress, isAddress } from "viem";

import type { BaseFormattedTransactionActivity } from "./interfaces";
import { decodeInstruction, decodeTransaction, getDecompiledInstructions, SOLANA_INSTRUCTION_TYPES } from "./solana";

type ActivityTransaction = BaseFormattedTransactionActivity & {
  etherscanLink?: string;
  explorerLink?: string;
  gas?: string;
  gasPrice?: string;
  nonce?: string;
  from_aa_address?: string;
};

type ActivityNft = {
  contractAddress?: string;
  contractImage?: string;
  contractFallbackLogo?: string;
  symbol?: string;
};

type RatesMap = Record<string, string | number>;
type SolanaRpc = Rpc<SolanaRpcApi>;

const TOKEN_LIKE_CATEGORIES = new Set<string>([
  TRANSACTION_TYPES.TOKEN_METHOD_TRANSFER,
  TRANSACTION_TYPES.TOKEN_METHOD_TRANSFER_FROM,
  TRANSACTION_TYPES.TOKEN_METHOD_APPROVE,
  TRANSACTION_TYPES.COLLECTIBLE_METHOD_SAFE_TRANSFER_FROM,
  TRANSACTION_TYPES.SET_APPROVAL_FOR_ALL,
]);

const WalletActivity = {
  send: "walletActivity.send",
  receive: "walletActivity.receive",
} as const;

type EthereumMapParams = {
  txMeta: EthereumTransactionMeta;
  currentAddresses: string[];
  blockExplorerUrl?: string;
  nativeTicker: string;
  currentCurrency: string;
  conversionRate: number;
  tokenRates: RatesMap;
  tokens: BaseTokenInfo[];
  nfts: ActivityNft[];
};

type FormatActivityTransactionsParams = {
  chainNamespace: string;
  selectedAddress: string;
  smartAccountAddress?: string;
  blockExplorerUrl?: string;
  currentCurrency: string;
  conversionRate: number;
  nativeTicker: string;
  rpcTarget?: string;
  transactionMetas: Array<EthereumTransactionMeta | SolanaTransactionMeta>;
  tokenRates: RatesMap;
  tokens: BaseTokenInfo[];
  nfts: ActivityNft[];
};

const EMPTY_CURRENCY = {
  currencyAmount: "",
  currencyAmountString: "",
};

const normalizeEvmAddress = (value?: string) => {
  if (!value) return "";
  return isAddress(value) ? getAddress(value).toLowerCase() : value.toLowerCase();
};

const matchTokenAddress = (target: string | undefined, candidate: string | undefined) => {
  if (!target || !candidate) return false;
  return target.toLowerCase() === candidate.toLowerCase();
};

const buildSlicedAddress = (value?: string) => {
  if (!value) return "";
  return addressSlicer(value) || value;
};

const toIsoDate = (value?: string | number) => {
  if (!value) return new Date(0).toISOString();
  return new Date(value).toISOString();
};

const toUpperCurrency = (currency: string) => currency.toUpperCase();

const getRate = (tokenAddress: string | undefined, tokenRates: RatesMap) => {
  if (!tokenAddress) return "";
  return tokenRates[tokenAddress] ?? tokenRates[tokenAddress.toLowerCase()] ?? tokenRates[tokenAddress.toUpperCase()] ?? "";
};

const getCurrencyAmounts = ({
  amount,
  tokenRate,
  conversionRate,
  currentCurrency,
}: {
  amount: string;
  tokenRate?: string | number;
  conversionRate: number;
  currentCurrency: string;
}) => {
  if (!amount) return EMPTY_CURRENCY;

  const fiatRate = new BigNumber(tokenRate ?? 1).times(conversionRate);
  if (!fiatRate.isFinite() || fiatRate.isZero()) return EMPTY_CURRENCY;

  const currencyAmount = new BigNumber(amount).times(fiatRate).toString();
  return {
    currencyAmount,
    currencyAmountString: `${significantDigits(Number(currencyAmount))} ${toUpperCurrency(currentCurrency)}`,
  };
};

const getCurrentAction = (from: string, to: string, currentAddresses: string[]) => {
  const normalizedFrom = normalizeEvmAddress(from);
  const normalizedTo = normalizeEvmAddress(to);
  if (currentAddresses.includes(normalizedTo) && !currentAddresses.includes(normalizedFrom)) {
    return WalletActivity.receive;
  }
  return WalletActivity.send;
};

const normalizeMethodAddress = (value: unknown) => {
  return typeof value === "string" ? value : "";
};

const normalizeMethodValue = (value: unknown) => {
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") return value;
  return "";
};

const buildBaseActivity = ({
  id,
  date,
  from,
  to,
  action,
  status,
  chainId,
  currentCurrency,
  transactionHash,
  transactionCategory,
}: {
  id: string;
  date: string;
  from: string;
  to: string;
  action: string;
  status: TransactionStatus;
  chainId: string;
  currentCurrency: string;
  transactionHash: string;
  transactionCategory: string;
}): ActivityTransaction => ({
  id,
  date,
  from,
  slicedFrom: buildSlicedAddress(from),
  to,
  slicedTo: buildSlicedAddress(to),
  action,
  totalAmount: "0",
  totalAmountString: "0",
  currencyAmount: "",
  currencyAmountString: "",
  amount: "0",
  status,
  currencyUsed: toUpperCurrency(currentCurrency),
  chainId,
  type: "",
  type_name: "",
  type_image_link: "",
  transaction_hash: transactionHash,
  transaction_category: transactionCategory,
  contract_address: "",
  is_cancel: transactionCategory === TRANSACTION_TYPES.CANCEL,
});

export function buildTransactionExplorerUrl(blockExplorerUrl?: string, transactionHash?: string): string {
  if (!blockExplorerUrl || !transactionHash) return "";

  try {
    const url = new URL(blockExplorerUrl);
    const basePath = url.pathname.replace(/\/+$/, "");
    url.pathname = `${basePath}/tx/${transactionHash}`;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
}

const mapEthereumTokenData = ({
  txMeta,
  token,
  nft,
  activity,
  currentCurrency,
  conversionRate,
  tokenRates,
}: {
  txMeta: EthereumTransactionMeta;
  token?: BaseTokenInfo;
  nft?: ActivityNft;
  activity: ActivityTransaction;
  currentCurrency: string;
  conversionRate: number;
  tokenRates: RatesMap;
}) => {
  const methodParams = txMeta.methodParams || [];
  const tokenAddress = txMeta.transaction.to || "";
  const category = txMeta.transactionCategory || activity.transaction_category;
  const contractType = txMeta.contractType || "";

  if (contractType === CONTRACT_TYPE_ERC721 || contractType === CONTRACT_TYPE_ERC1155 || nft) {
    activity.contract_address = tokenAddress;
    activity.type = contractType || CONTRACT_TYPE_ERC721;
    activity.type_name = token?.symbol || nft?.symbol || "NFT";
    activity.type_image_link = nft?.contractImage || nft?.contractFallbackLogo || "";
    activity.totalAmount = "1";
    activity.totalAmountString = "1";
    activity.amount = "1";
    activity.currencyAmount = "";
    activity.currencyAmountString = "";
    activity.token_id =
      normalizeMethodValue(methodParams[2]) || normalizeMethodValue(methodParams[3]) || normalizeMethodValue(methodParams[0]) || undefined;
    if (category === TRANSACTION_TYPES.COLLECTIBLE_METHOD_SAFE_TRANSFER_FROM) {
      activity.to = normalizeMethodAddress(methodParams[1]) || activity.to;
      activity.slicedTo = buildSlicedAddress(activity.to);
    }
    return activity;
  }

  if (token) {
    const decimals = Number(token.decimals || 0);
    const amountRaw =
      normalizeMethodValue(methodParams[2]) || normalizeMethodValue(methodParams[1]) || normalizeMethodValue(txMeta.transaction.value);
    const totalAmount = amountRaw ? new BigNumber(amountRaw).shiftedBy(-decimals).toString() : "0";
    const currency = getCurrencyAmounts({
      amount: totalAmount,
      tokenRate: getRate(token.tokenAddress, tokenRates),
      conversionRate,
      currentCurrency,
    });

    activity.contract_address = tokenAddress;
    activity.type = (token.symbol || token.name || "token").toLowerCase();
    activity.type_name = token.symbol || token.name || "Token";
    activity.type_image_link = token.logo || "";
    activity.totalAmount = totalAmount;
    activity.totalAmountString = totalAmount;
    activity.amount = totalAmount;
    activity.currencyAmount = currency.currencyAmount;
    activity.currencyAmountString = currency.currencyAmountString;
    activity.to = normalizeMethodAddress(methodParams[1]) || normalizeMethodAddress(methodParams[0]) || activity.to;
    activity.slicedTo = buildSlicedAddress(activity.to);
    return activity;
  }

  return activity;
};

export function mapEthereumTransactionMetaToActivity({
  txMeta,
  currentAddresses,
  blockExplorerUrl,
  nativeTicker,
  currentCurrency,
  conversionRate,
  tokenRates,
  tokens,
  nfts,
}: EthereumMapParams): ActivityTransaction {
  const transactionHash = txMeta.transactionHash || txMeta.userOpHash || txMeta.id;
  const from = txMeta.transaction.from || "";
  const to = txMeta.transaction.to || "";
  const category = txMeta.transactionCategory || txMeta.transactionType || TRANSACTION_TYPES.STANDARD_TRANSACTION;
  const action = getCurrentAction(from, to, currentAddresses);
  const activity = buildBaseActivity({
    id: txMeta.id,
    date: toIsoDate(txMeta.updated_at || txMeta.time),
    from,
    to,
    action,
    status: txMeta.status,
    chainId: txMeta.chainId,
    currentCurrency,
    transactionHash,
    transactionCategory: category,
  });

  activity.input = txMeta.transaction.data || "";
  activity.etherscanLink = buildTransactionExplorerUrl(blockExplorerUrl, transactionHash);
  activity.gas = txMeta.transaction.gas || txMeta.transaction.gasLimit || txMeta.transaction.callGasLimit || "0x0";
  activity.gasPrice = txMeta.transaction.gasPrice || txMeta.transaction.maxFeePerGas || "0x0";
  activity.nonce = txMeta.transaction.nonce || "";
  activity.from_aa_address = txMeta.isUserOperation ? txMeta.transaction.from : undefined;

  const token = tokens.find((item) => matchTokenAddress(item.tokenAddress, txMeta.transaction.to));
  const nft = nfts.find((item) => matchTokenAddress(item.contractAddress, txMeta.transaction.to));
  const hasTokenLikeData =
    !!token ||
    !!nft ||
    txMeta.contractType === CONTRACT_TYPE_ERC721 ||
    txMeta.contractType === CONTRACT_TYPE_ERC1155 ||
    TOKEN_LIKE_CATEGORIES.has(category);

  if (hasTokenLikeData) {
    return mapEthereumTokenData({
      txMeta,
      token,
      nft,
      activity,
      currentCurrency,
      conversionRate,
      tokenRates,
    });
  }

  const nativeAmount = txMeta.transaction.value ? formatEther(BigInt(txMeta.transaction.value)) : "0";
  const currency = getCurrencyAmounts({
    amount: nativeAmount,
    conversionRate,
    currentCurrency,
  });

  activity.type = nativeTicker.toLowerCase();
  activity.type_name = nativeTicker.toUpperCase();
  activity.totalAmount = nativeAmount;
  activity.totalAmountString = nativeAmount;
  activity.amount = nativeAmount;
  activity.currencyAmount = currency.currencyAmount;
  activity.currencyAmountString = currency.currencyAmountString;
  return activity;
}

const mapSolanaTransactionMetaToActivity = async ({
  txMeta,
  selectedAddress,
  blockExplorerUrl,
  nativeTicker,
  currentCurrency,
  conversionRate,
  rpc,
  tokenRates,
  tokens,
}: {
  txMeta: SolanaTransactionMeta;
  selectedAddress: string;
  blockExplorerUrl?: string;
  nativeTicker: string;
  currentCurrency: string;
  conversionRate: number;
  rpc?: SolanaRpc;
  tokenRates: RatesMap;
  tokens: BaseTokenInfo[];
}): Promise<ActivityTransaction> => {
  const transactionHash = txMeta.transactionHash || txMeta.id;
  const activity = buildBaseActivity({
    id: txMeta.id,
    date: toIsoDate(txMeta.updated_at || txMeta.time),
    from: selectedAddress,
    to: "",
    action: WalletActivity.send,
    status: txMeta.status,
    chainId: txMeta.chainId,
    currentCurrency,
    transactionHash,
    transactionCategory: TRANSACTION_TYPES.STANDARD_TRANSACTION,
  });

  activity.type = nativeTicker.toLowerCase();
  activity.type_name = nativeTicker.toUpperCase();
  activity.explorerLink = buildTransactionExplorerUrl(blockExplorerUrl, transactionHash);
  activity.gas = txMeta.gas || "";
  activity.nonce = "";

  if (!rpc) return activity;

  try {
    const { tx } = decodeTransaction(txMeta.transaction.serializedTransaction);
    const instructions = await getDecompiledInstructions(tx, rpc);
    const decodedInstructions = instructions.map((instruction) => decodeInstruction(instruction));
    const decodedInstruction = decodedInstructions[0];

    if (!decodedInstruction) return activity;

    if (decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.SYSTEM_TRANSFER_SOL) {
      const decodedData = decodedInstruction.data as { source?: string; destination?: string; amount?: bigint };
      const totalAmount = new BigNumber((decodedData.amount || 0n).toString()).shiftedBy(-9).toString();
      const currency = getCurrencyAmounts({
        amount: totalAmount,
        conversionRate,
        currentCurrency,
      });
      activity.from = decodedData.source || selectedAddress;
      activity.slicedFrom = buildSlicedAddress(activity.from);
      activity.to = decodedData.destination || "";
      activity.slicedTo = buildSlicedAddress(activity.to);
      activity.action = activity.to === selectedAddress ? WalletActivity.receive : WalletActivity.send;
      activity.totalAmount = totalAmount;
      activity.totalAmountString = totalAmount;
      activity.amount = totalAmount;
      activity.currencyAmount = currency.currencyAmount;
      activity.currencyAmountString = currency.currencyAmountString;
      activity.transaction_category = TRANSACTION_TYPES.SENT_SOL;
      return activity;
    }

    if (
      decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_TRANSFER ||
      decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_TRANSFER_CHECKED ||
      decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_2022_TRANSFER ||
      decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_2022_TRANSFER_CHECKED
    ) {
      const decodedData = decodedInstruction.data as {
        source?: string;
        destination?: string;
        amount?: number | bigint;
        mint?: string;
        decimals?: number;
      };

      if (decodedData.source && decodedData.destination) {
        const isToken2022 =
          decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_2022_TRANSFER ||
          decodedInstruction.type === SOLANA_INSTRUCTION_TYPES.TOKEN_2022_TRANSFER_CHECKED;
        const fetchTokenAccount = isToken2022 ? fetchMaybeToken2022 : fetchMaybeToken;
        const [sourceToken, destinationToken] = await Promise.all([
          fetchTokenAccount(rpc, address(decodedData.source)),
          fetchTokenAccount(rpc, address(decodedData.destination)),
        ]);

        const mint = decodedData.mint || (sourceToken.exists ? sourceToken.data.mint : "");
        const token = tokens.find((item) => matchTokenAddress(item.tokenAddress, mint));
        const decimals = Number(decodedData.decimals ?? token?.decimals ?? 0);
        const totalAmount = new BigNumber((decodedData.amount || 0).toString()).shiftedBy(-decimals).toString();
        const currency = getCurrencyAmounts({
          amount: totalAmount,
          tokenRate: getRate(mint, tokenRates),
          conversionRate,
          currentCurrency,
        });

        activity.from = sourceToken.exists ? sourceToken.data.owner : decodedData.source;
        activity.slicedFrom = buildSlicedAddress(activity.from);
        activity.to = destinationToken.exists ? destinationToken.data.owner : decodedData.destination;
        activity.slicedTo = buildSlicedAddress(activity.to);
        activity.action = activity.to === selectedAddress ? WalletActivity.receive : WalletActivity.send;
        activity.totalAmount = totalAmount;
        activity.totalAmountString = totalAmount;
        activity.amount = totalAmount;
        activity.currencyAmount = currency.currencyAmount;
        activity.currencyAmountString = currency.currencyAmountString;
        activity.transaction_category = TRANSACTION_TYPES.TOKEN_METHOD_TRANSFER;
        activity.contract_address = mint;
        activity.type = (token?.symbol || "token").toLowerCase();
        activity.type_name = token?.symbol || token?.name || "Token";
        activity.type_image_link = token?.logo || "";
      }
    }
  } catch {
    return activity;
  }

  return activity;
};

/**
 * Format activity transactions from transaction controller for display
 */
export async function formatActivityTransactions({
  chainNamespace,
  selectedAddress,
  smartAccountAddress,
  blockExplorerUrl,
  currentCurrency,
  conversionRate,
  nativeTicker,
  rpcTarget,
  transactionMetas,
  tokenRates,
  tokens,
  nfts,
}: FormatActivityTransactionsParams): Promise<BaseFormattedTransactionActivity[]> {
  if (!selectedAddress) return [];

  const currentAddresses = [selectedAddress, smartAccountAddress || ""].filter(Boolean).map((value) => normalizeEvmAddress(value));

  if (chainNamespace === CHAIN_NAMESPACES.EIP155) {
    return transactionMetas
      .map((txMeta) => txMeta as EthereumTransactionMeta)
      .filter((txMeta) => {
        const from = normalizeEvmAddress(txMeta.transaction.from);
        const to = normalizeEvmAddress(txMeta.transaction.to);
        return currentAddresses.includes(from) || currentAddresses.includes(to) || !txMeta.transaction.from;
      })
      .map((txMeta) =>
        mapEthereumTransactionMetaToActivity({
          txMeta,
          currentAddresses,
          blockExplorerUrl,
          nativeTicker,
          currentCurrency,
          conversionRate,
          tokenRates,
          tokens,
          nfts,
        })
      )
      .sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime());
  }

  const rpc = rpcTarget ? createSolanaRpc(rpcTarget) : undefined;
  const solanaTransactions = await Promise.all(
    transactionMetas.map((txMeta) =>
      mapSolanaTransactionMetaToActivity({
        txMeta: txMeta as SolanaTransactionMeta,
        selectedAddress,
        blockExplorerUrl,
        nativeTicker,
        currentCurrency,
        conversionRate,
        rpc,
        tokenRates,
        tokens,
      })
    )
  );

  return solanaTransactions.sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime());
}
